import { lazy, Suspense } from 'react';
import { createBrowserRouter, createRoutesFromElements, Outlet, Route, RouterProvider } from 'react-router-dom';
// REV18 (docs/specs/REV18-switch-classnames-toast-theme.md): `react-toastify/unstyled`, not the
// package root. The styled entry's `<ToastContainer>` self-injects react-toastify's own default
// CSS as an unlayered `<style>` tag on mount (react-toastify >=10's "you don't have to import our
// CSS" convenience) — appended to `<head>` after page load, so it always won the cascade over our
// themed overrides in index.css regardless of import order or `@layer` tricks. `unstyled` has the
// identical API with that self-injection stripped, leaving our own `ReactToastify.css` import
// (main.tsx) + index.css overrides as the only styling source. `lib/events.ts`'s `toast()` import
// has to match this entry point so both talk to the same toast registry.
import { ToastContainer } from 'react-toastify/unstyled';
import { ComingSoon } from './components/ComingSoon';
import { RequireAuth } from './components/RequireAuth';
import { DesignShell } from './designs/DesignShell';
import { Bosses } from './routes/Bosses';
import { CharacterDetail } from './routes/CharacterDetail';
import { Characters } from './routes/Characters';
import { Checklist } from './routes/Checklist';
import { Dashboard } from './routes/Dashboard';
import { Hexa } from './routes/Hexa';
import { Legion } from './routes/Legion';
import { Links } from './routes/Links';
import { Login } from './routes/Login';
import { Pitched } from './routes/Pitched';
import { Privacy } from './routes/Privacy';
import { Settings } from './routes/Settings';
import { TaskSetup } from './routes/TaskSetup';

/** REV23 (docs/specs/REV23-share-profile.md): lazy — a public, unauthenticated route that must
 *  never land in the main bundle's eager-load path, same reasoning as `Characters.tsx`'s
 *  `ScreenshotImportModal`. */
const SharedProfile = lazy(() =>
  import('./routes/SharedProfile').then((m) => ({ default: m.SharedProfile })),
);

/** REV30: fixture-driven style bench for the A/B/C designs — dev builds only, so the import (and
 *  the route) are dead code in production and never reach the bundle. */
const DesignLab = import.meta.env.DEV
  ? lazy(() => import('./routes/DesignLab').then((m) => ({ default: m.DesignLab })))
  : null;

/** Everything except `/login` needs a session. */
function ProtectedLayout() {
  return (
    <RequireAuth>
      <DesignShell>
        <Outlet />
      </DesignShell>
    </RequireAuth>
  );
}

/** REV29 (docs/specs/REV29-motion-patterns.md §1a): keeps `<ToastContainer>` mounted across
 *  navigations exactly as before the data-router migration — it now lives on a root layout route
 *  instead of sitting next to `<Routes>`. */
function RootLayout() {
  return (
    <>
      <Outlet />
      {/* REV16: replaces the in-house `<Toaster />` — bottom-right, offset clear of the mobile tab
          bar below `md` (see index.css's `.Toastify__toast-container--bottom-right` override).
          `role="status"` matches the old toaster's own role so existing rollback/error-toast
          assertions keep working unchanged; `autoClose` mirrors the old 6s dismiss timer. */}
      <ToastContainer position="bottom-right" role="status" autoClose={6000} closeOnClick />
    </>
  );
}

export const appRoutes = createRoutesFromElements(
  <>
    <Route element={<RootLayout />}>
      <Route path="/login" element={<Login />} />
      {/* Public on purpose: a privacy notice behind a login wall is not a privacy notice. */}
      <Route path="/privacy" element={<Privacy />} />
      {/* REV23: the public share-profile link — outside ProtectedLayout, same as /privacy, so an
          anonymous visitor never hits RequireAuth's session probe/redirect. */}
      <Route
        path="/s/:token"
        element={
          <Suspense fallback={<div className="p-6 text-sm text-text-muted">Loading…</div>}>
            <SharedProfile />
          </Suspense>
        }
      />

      {DesignLab && (
        <Route
          path="/design-lab"
          element={
            <Suspense fallback={null}>
              <DesignLab />
            </Suspense>
          }
        />
      )}

      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/checklist" element={<Checklist />} />
        <Route path="/checklist/manage" element={<TaskSetup />} />
        <Route path="/bosses" element={<Bosses />} />
        <Route path="/pitched" element={<Pitched />} />
        <Route path="/characters" element={<Characters />} />
        <Route path="/characters/:id" element={<CharacterDetail />} />
        <Route path="/links" element={<Links />} />
        <Route path="/legion" element={<Legion />} />
        <Route path="/hexa" element={<Hexa />} />
        <Route path="/settings" element={<Settings />} />
        <Route
          path="*"
          element={<ComingSoon title="Not found" note="That page does not exist (yet)." />}
        />
      </Route>
    </Route>
  </>,
);

const router = createBrowserRouter(appRoutes);

export default function App() {
  return <RouterProvider router={router} />;
}
